import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, CircularProgress, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback, useMemo, useState } from "react";
import { BADGE, INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { useStatusColor } from "../hooks/useThresholdStatus";
import type { ThresholdConfig } from "../types";

export type ProgressItem = {
	title: string;
	wanted: number;
	actual: number;
	onDoubleClick?: () => void;
};

export type InHouseProgressChartProps = {
	title: string;
	items: ProgressItem[];
	threshold: ThresholdConfig;
	showViewAll?: boolean;
	onViewAll?: () => void;
	maxVisibleItems?: number;
	hideWanted?: boolean;
	statTitle?: string;
	sortByBest?: boolean;
	isLoading?: boolean;
	onDownload?: () => Promise<void>;
};

const ProgressItemComponent: FC<{ item: ProgressItem; threshold: ThresholdConfig; hideWanted?: boolean }> = ({ item, threshold, hideWanted }) => {
	const percentage = (item.actual / item.wanted) * 100;
	const getStatusColor = useStatusColor({ threshold, mode: "higherIsBetter" });
	const { color } = getStatusColor(percentage);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				marginBottom: 1,
			}}
		>
			{/* Title */}
			<Typography
				onDoubleClick={item.onDoubleClick}
				sx={{
					fontSize: 14,
					color: INK.SECONDARY,
					fontWeight: 400,
					minWidth: "120px",
					cursor: item.onDoubleClick ? "pointer" : "default",
					"&:hover": item.onDoubleClick
						? {
								textDecoration: "underline",
							}
						: {},
				}}
			>
				{item.title}
			</Typography>

			{/* Progress bar */}
			<Box
				sx={{
					flex: 1,
					height: "20px",
					backgroundColor: NEUTRAL[300],
					borderRadius: BADGE.PILL_RADIUS_FULL,
					overflow: "hidden",
					position: "relative",
				}}
			>
				<Box
					sx={{
						width: `${Math.min(percentage, 100)}%`,
						height: "100%",
						backgroundColor: color,
						borderRadius: BADGE.PILL_RADIUS_FULL,
						transition: "width 0.3s ease",
					}}
				/>
			</Box>

			{/* Fraction display */}
			<Typography
				sx={{
					fontSize: 14,
					color: NEUTRAL[600],
					fontWeight: 400,
					minWidth: "60px",
					textAlign: "right",
				}}
			>
				{item.actual}
				{!hideWanted ? ` / ${item.wanted}` : ""}
			</Typography>
		</Box>
	);
};

export const InHouseProgressChart: FC<InHouseProgressChartProps> = ({
	title,
	items,
	threshold,
	showViewAll = true,
	onViewAll,
	maxVisibleItems = 5,
	hideWanted = false,
	statTitle,
	sortByBest = true,
	isLoading = false,
	onDownload,
}) => {
	const [isDownloading, setIsDownloading] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const handleDownload = useCallback(async () => {
		if (!onDownload || isDownloading) return;
		setIsDownloading(true);
		try {
			await onDownload();
		} finally {
			setIsDownloading(false);
		}
	}, [onDownload, isDownloading]);

	const { topItems, bottomItems, shouldShowViewAll, hasMiddleItems } = useMemo(() => {
		const itemsWithPercentage = items.map((item) => ({
			...item,
			percentage: (item.actual / item.wanted) * 100,
		}));

		const sortedItems = itemsWithPercentage.sort((a, b) => (sortByBest ? b.percentage - a.percentage : a.percentage - b.percentage));

		const shouldShowViewAllLocal = sortedItems.length > maxVisibleItems;
		const halfCount = Math.floor(maxVisibleItems / 2);

		const top = shouldShowViewAllLocal ? sortedItems.slice(0, halfCount) : sortedItems.slice(0, maxVisibleItems);
		const bottom = shouldShowViewAllLocal ? sortedItems.slice(-halfCount) : [];

		return {
			topItems: top,
			bottomItems: bottom,
			shouldShowViewAll: shouldShowViewAllLocal,
			hasMiddleItems: shouldShowViewAllLocal && sortedItems.length > maxVisibleItems,
		};
	}, [items, maxVisibleItems, sortByBest]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				padding: 2,
				backgroundColor: SURFACE,
				borderRadius: 2,
				width: "100%",
				height: "100%",
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* Header with title and "Current" label */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography
						sx={{
							fontSize: 20,
							fontWeight: 700,
							color: INK.SECONDARY,
						}}
					>
						{title}
					</Typography>
					{onDownload && (
						<Tooltip title="Download Excel">
							<IconButton
								onClick={handleDownload}
								disabled={isDownloading}
								sx={{
									opacity: isHovered ? 1 : 0,
									transition: "opacity 0.2s ease-in-out",
									padding: 0.5,
								}}
							>
								{isDownloading ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon sx={{ color: INK.SECONDARY }} />}
							</IconButton>
						</Tooltip>
					)}
				</Box>
				<Typography
					sx={{
						fontSize: 12,
						color: alpha(INK.SECONDARY, 0.5),
						fontWeight: 400,
					}}
				>
					{statTitle ?? "Current"}
				</Typography>
			</Box>

			{isLoading ? (
				<Box sx={{ flex: 1 }}>
					<Skeleton variant="rectangular" width="100%" height="100%" sx={{ minHeight: "400px" }} />
				</Box>
			) : (
				<>
					{/* Progress items */}
					<Box sx={{ flex: 1, overflow: "hidden" }}>
						{/* Top items */}
						{topItems.map((item, idx) => (
							<ProgressItemComponent key={`${item.title}-${idx}`} item={item} threshold={threshold} hideWanted={hideWanted} />
						))}

						{/* Dashed divider when showing top and bottom */}
						{hasMiddleItems && (
							<Box
								sx={{
									borderTop: `2px dashed ${NEUTRAL[300]}`,
									margin: "16px 0",
								}}
							/>
						)}

						{/* Bottom items */}
						{bottomItems.map((item, idx) => (
							<ProgressItemComponent key={`${item.title}-${idx}`} item={item} threshold={threshold} hideWanted={hideWanted} />
						))}
					</Box>

					{/* View All button */}
					{showViewAll && shouldShowViewAll && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								marginTop: "auto",
							}}
						>
							<Typography
								onClick={onViewAll}
								sx={(theme) => ({
									fontSize: 14,
									fontWeight: 700,
									color: theme.palette.primary.main,
									cursor: "pointer",
									"&:hover": onViewAll
										? {
												textDecoration: "underline",
											}
										: {},
								})}
							>
								View All
							</Typography>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

export default InHouseProgressChart;
